import './style.scss';

import { Alert, Spinner } from '@amzn/awsui-components-react';
import { Data, Layout, PlotData } from 'plotly.js';
import PlotlyBasic from 'plotly.js-basic-dist';
import { useEffect, useState } from 'react';
import createPlotlyComponent from 'react-plotly.js/factory';

import { DashboardChart, Partition } from '../../../common/DashboardLocalStorage';
import { constants } from '../../../common/Localization';
import { ChartFunctions, ChartTypes, defaultLayout, MAX_CHART_DATA_LENGTH } from './constants';
import { Series } from './interfaces';

const PlotlyChart = createPlotlyComponent(PlotlyBasic);

interface Props<T> {
    dashboardChart: DashboardChart;
    dataSource: T[];
    dataLoading: boolean;
    containerRef: React.RefObject<HTMLDivElement>;
}

export function Chart<T>({ dashboardChart, dataSource, dataLoading, containerRef }: Props<T>): JSX.Element {

    const [ hasData, setHasData ] = useState(false);
    const [ chartData, setChartData ] = useState<Partial<Data>[]>([]);
    const [ category, setCategory ] = useState('');
    const [ partition, setPartition ] = useState<Partition | null>(null);
    const [ layout, setLayout ] = useState<Partial<Layout>>(defaultLayout);
    const [ withLabels, setWithLabels ] = useState(false);
    const [ chartProperty, setChartProperty ] = useState<string>(dashboardChart.value.prop);
    const [ chartFunction, setChartFunction ] = useState<ChartFunctions>(dashboardChart.value.fn);
    const [ chartType, setChartType ] = useState<ChartTypes>(dashboardChart.type);

    useEffect(() => {
        setChartProperty(dashboardChart.value.prop);
        setChartFunction(dashboardChart.value.fn);
        setChartType(dashboardChart.type);
        const category = typeof dashboardChart.category === 'string' ? dashboardChart.category : dashboardChart.category.prop;
        setCategory(category);
        if (typeof dashboardChart.category !== 'string' && dashboardChart.category.partition) {
            setPartition(dashboardChart.category.partition);
        }
        setWithLabels(dashboardChart.withLabels ?? false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ dashboardChart ]);

    useEffect(() => {
        if (category.length > 0) {
            refresh();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ category, partition, chartProperty, chartFunction, chartType ]);

    useEffect(() => {
        setLabels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ withLabels ]);

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }
        const updateLayout: Partial<Layout> = {...layout, width: containerRef.current.clientWidth};
        setLayout(updateLayout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ containerRef.current ]);

    /**
     * Returns the value stored in the category property, or 'Unavailable *' if not found
     * @param data 
     * @returns 
     */
    const getCategoryValue = (data: T): string => {
        const value = data[category as keyof T];
        return value ? String(value) : `${constants.UNAVAILABLE} *`;
    };

    const getPartitionIndex = (value: number): number => {
        if (!partition) {
            return -1;
        }
        let i: number;
        for (i = 0; i < partition.upperBounds.length; ++i) {
            if (value <= partition.upperBounds[i]) break;
        }
        return i;
    };

    /**
     * Returns the category value when a partition is set
     * @param data 
     * @returns 
     */
    const getCategoryValueWithPartition = (data: T): string => {
        const value = getCategoryValue(data);
        try {
            const n = parseFloat(value as string);
            if (isNaN(n)) {
                return value;
            }
            const index = getPartitionIndex(n);
            return partition?.labels[index] ?? '';
        } catch (err) {
            return '';
        }
    };

    /**
     * Returns the data stored in the given property, as a string or number
     * @param data 
     * @returns 
     */
    const getPropertyValue = (data: T): string | number => {
        const value = data[chartProperty as keyof T];
        if (!value) {
            return '';
        } else {
            const n = Number(value);
            if (isNaN(n)) {
                return String(value);
            } else {
                return n;
            }
        }
    };

    /**
     * Set the data series to the chart
     */
    const addSeries = (series: Series[]) => {
        const plotData: Partial<PlotData> = {
            marker:{
                color: [
                    '#FF7F0E', '#1F77B4', '#2CA02C', '#D62728',
                    '#9467BD', '#8C564B', '#E377C2', '#7F7F7F',
                    '#BCBD22', '#17BECF', '#FFBB78', '#AEC7E8',
                    '#98DF8A', '#FF9896', '#C5B0D5', '#C49C94',
                    '#F7B6D2', '#C7C7C7', '#DBDB8D', '#9EDAE5'
                ]
            }
        };

        const categories = series.map(s => s.category);
        const values = series.map(s => s.value);

        switch (chartType) {
            case ChartTypes.PIE:
                plotData.type = 'pie';
                plotData.labels = categories;
                plotData.values = values;
                break;
            case ChartTypes.DOUGHNUT:
                plotData.type = 'pie';
                plotData.hole = 0.4;
                plotData.labels = categories;
                plotData.values = values;
                break;
            case ChartTypes.BAR:
                plotData.type = 'bar';
                plotData.orientation = 'v';
                plotData.x = categories;
                plotData.y = values;
                break;
            case ChartTypes.HORIZONTAL_BAR:
                plotData.type = 'bar';
                plotData.orientation = 'h';
                plotData.x = values;
                plotData.y = categories;
                break;
            case ChartTypes.LINE:
                plotData.type = 'scatter';
                plotData.x = categories;
                plotData.y = values;
                break;
        }

        setChartData([plotData]);
    };

    useEffect(() => {
        // update layout after setting the chart data so the range can be automatically set based on the chart data
        setPlotLayout();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ chartData ]);

    /**
     * Set the layout values for the chart depending on the type of chart
     */
    const setPlotLayout = () => {
        let updateLayout: Partial<Layout>;
        switch (chartType) {
            case ChartTypes.PIE:
                break;
            case ChartTypes.DOUGHNUT:
                updateLayout = { ...layout, margin: {t: 20, r: 20, b: 20, l: 20}};
                setLayout(updateLayout);
                break;
            case ChartTypes.BAR:
                updateLayout = { ...layout, xaxis: {...layout.xaxis, type: 'category'}, yaxis: {...layout.yaxis, type: undefined}};
                setLayout(updateLayout);
                break;
            case ChartTypes.HORIZONTAL_BAR:
                updateLayout = { ...layout, yaxis: {...layout.yaxis, type: 'category'}, xaxis: {...layout.xaxis, type: undefined}};
                setLayout(updateLayout);
                break;
            case ChartTypes.LINE:
                break;
        }
    };
    
    /**
     * Reduce the array of data based on the chart function
     * @param values 
     * @returns number
     */
    const reduceArray = (values:  (string | number)[]): number => {
        switch(chartFunction) {
            case ChartFunctions.MIN:
                return Math.min.apply(values);
            case ChartFunctions.MAX:
                return Math.max.apply(values);
            case ChartFunctions.DISTINCT:
                return new Set(values).values.length;
            case ChartFunctions.AVERAGE:
                return Number(values.reduce((a,b) => Number(a) + Number(b), 0))/values.length ?? 0;
            case ChartFunctions.SUM:
                return Number(values.reduce((a,b) => Number(a) + Number(b), 0));
            case ChartFunctions.COUNT:
            default:
                return values.length;
        }
    };

    /**
     * Creates the series of data displayed in the plot from the given dataSource
     * @returns void
     */
    const refresh = (): void => {
        if (!dataSource || dataSource.length === 0) {
            setHasData(false);
            return;
        }
        try {
            const groupedData = dataSource.reduce((mapped, current) => {
                const categoryValue = partition ? getCategoryValueWithPartition(current) : getCategoryValue(current);
                const propertyValues = mapped.get(categoryValue) ?? [];
                const value = getPropertyValue(current);
                propertyValues.push(value);
                mapped.set(categoryValue, propertyValues);
                return mapped;
            }, new Map<string, (string | number)[]>());

            const series: Series[] = [];
            groupedData.forEach((value, key) => {
                const reducedData = reduceArray(value);
                series.push({category: key, value: reducedData});
            });

            series.sort((a,b) => (a.category > b.category) ? 1 : -1);
            if (series && series.length > 0 && series.length < MAX_CHART_DATA_LENGTH) {
                addSeries(series);
                setHasData(true);
            } else {
                setHasData(false);
            }
        } catch (err) {
            setHasData(false);
        }
    };

    const setLabels = () => {
        let updateLayout: Partial<Layout> = {...layout, width: containerRef.current?.clientWidth};
        if (withLabels) {
            updateLayout = {...updateLayout, xaxis: {...layout.xaxis, showticklabels: true}, yaxis: {...layout.yaxis, showticklabels: true}};
            updateLayout.showlegend = ['pie', 'doughnut'].includes(chartType);
        } else {
            updateLayout = {...updateLayout, xaxis: {...layout.xaxis, showticklabels: false}, yaxis: {...layout.yaxis, showticklabels: false}};
            updateLayout.showlegend = false;
        }
        setLayout(updateLayout);
    };

    if (dataLoading) {
        return <Spinner/>;
    } else if (hasData) { 
        return (
            <PlotlyChart className='plotly-chart' data={chartData} layout={layout} useResizeHandler={true} />
        );
    } else { 
        return (
            <Alert 
                type='warning'
                dismissible={false}
                header={constants.CANNOT_RENDER_CHART}
            >
                {constants.INSUFFICIENT_DATA_WARNING}
            </Alert>
        );
    }
}
